import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { HouseholdSidePanelComponent } from '@se-po/se-my-sportsengine-feature-household-side-panel'
import { NewGuardianModalComponent } from '@se-po/se-my-sportsengine-feature-new-guardian-modal'
import { NewPersonModalComponent } from '@se-po/se-my-sportsengine-feature-new-person-modal'
import {
  AnalyticsService,
  HouseholdProfilesService,
  HouseholdRelatedGuardiansService,
  UserPreferenceService,
  WebViewService
} from '@se-po/shared-data-access-services'
import { EditPersonaModalComponent } from '@se-po/shared-feature-edit-persona-modal'
import { MergePersonaModalComponent } from '@se-po/shared-feature-merge-persona-modal'
import { RemovePersonaModalComponent } from '@se-po/shared-feature-remove-persona-modal'
import { Observable, Subscription, lastValueFrom, map, tap } from 'rxjs'
import { Guardianship, PersonaAccount, RelatedGuardiansResult, UserProfile } from 'se-resource-types/dist/lib/CentralService/Households'
import { Persona } from 'se-resource-types/dist/lib/CentralService/Profiles'

@Component({
  selector: 'se-po-household',
  templateUrl: './household.component.html',
  styleUrls: ['./household.component.scss'],
  standalone: false,
})
export class HouseholdComponent implements OnDestroy, OnInit, AfterViewInit {
  @ViewChild('newPersonModal') public newPersonModal: NewPersonModalComponent
  @ViewChild('householdSidePanel') public householdSidePanel: HouseholdSidePanelComponent
  @ViewChild('editPersonaModal') public editPersonaModal: EditPersonaModalComponent
  @ViewChild('mergePersonaModal') public mergePersonaModal: MergePersonaModalComponent
  @ViewChild('newGuardianModal') public newGuardianModal: NewGuardianModalComponent
  @ViewChild('removePersonaModal') public removePersonaModal: RemovePersonaModalComponent

  public adultProfiles: UserProfile[]
  public allGuardians: RelatedGuardiansResult[]
  public childProfiles: UserProfile[]
  public displayHeader = false
  public email: string
  public managerGuardians: RelatedGuardiansResult[]
  public mySeVersion: number
  public ready = false
  public selectedSubProfile: Persona
  public selfProfile: UserProfile

  private _profiles: UserProfile[]
  private _selectedPersonaId: number = null
  private _subscriptions: Subscription[] = []

  constructor(
    public cdr: ChangeDetectorRef,
    private userPreferenceService: UserPreferenceService,
    private householdProfilesService: HouseholdProfilesService,
    private householdRelatedGuardiansService: HouseholdRelatedGuardiansService,
    private route: ActivatedRoute,
    private router: Router,
    private analyticsService: AnalyticsService,
    private webViewService: WebViewService
  ) { }

  public get profiles(): UserProfile[] {
    return this._profiles
  }

  public get selectedPersonaId(): number {
    return this._selectedPersonaId
  }

  public set profiles(profiles: UserProfile[]) {
    this._profiles = profiles
    this.selfProfile = profiles.find(p => p.relationship === 'self')
    this.childProfiles = profiles.filter(p => p.relationship === 'other' && !!p.persona.age && p.persona.age < 18)
    this.adultProfiles = profiles.filter(p => p.relationship === 'other' && (!p.persona.age || p.persona.age >= 18))
  }

  public set selectedPersonaId(selectedPersonaId: number) {
    if (selectedPersonaId !== this.selectedPersonaId) {
      this._selectedPersonaId = selectedPersonaId
      this.setIdParam(this.selectedPersonaId || null)
    }
  }

  public async ngOnInit(): Promise<void> {
    this.displayHeader = !this.webViewService.current
    let personaId: number
    const householdProfiles = this.route.snapshot.data.userProfiles?.profiles
    this.email = this.route.snapshot.queryParams?.email

    if (this.route.snapshot.queryParams.id) {
      personaId = Number(this.route.snapshot.queryParams.id)
    }

    if (personaId) {
      this.selectedSubProfile = householdProfiles?.find(
        p => p.persona.id === personaId && (p.access !== 'viewer' || p.relationship !== 'self')
      )?.persona
    }

    this.getHousehold(householdProfiles, personaId)
  }

  public ngAfterViewInit(): void {
    if (!this.email) return

    if (this.selectedSubProfile) {
      this.newGuardianModal.open(this.selectedSubProfile, this.email)
      this.cdr.detectChanges()
    }
    else {
      // clear the email param if there is no valid sub profile to suggest a guardian for
      this.clearEmailParam()
    }
  }

  public ngOnDestroy(): void {
    this._subscriptions.forEach(subscription => subscription.unsubscribe())
    this._subscriptions.length = 0
  }

  public async getHousehold(profiles?: UserProfile[], id?: number): Promise<void> {
    const personaId = id || this.selectedPersonaId
    this.profiles = profiles ? profiles : await lastValueFrom(this.getProfiles())
    this.managerGuardians = await lastValueFrom(this.getGuardians())
    if (personaId) {
      const profile = this.profiles.find(p => p.persona.id === personaId)
        || this.managerGuardians.find(g => g.user.self_persona.id === personaId)
      if (profile) {
        this.selectedPersonaId = personaId
      }
      else if (this.selectedPersonaId) {
        this.selectedPersonaId = null
      }
    }
    this.ready = true
  }

  public getProfiles(): Observable<UserProfile[]> {
    return this.householdProfilesService.findMyProfiles()
  }

  public getGuardians(): Observable<RelatedGuardiansResult[]> {
    return this.householdRelatedGuardiansService.mine().pipe(
      tap(guardians => this.allGuardians = guardians),
      // we only want guardians with manager access in the household display
      map(guardians => guardians
        .filter(g => (g.profiles || []).some(p => p.access === 'manager'))
        .filter(g => g.user?.self_persona?.id !== this.selfProfile?.persona?.id))
    )
  }

  public onChosen(p: UserProfile | PersonaAccount): void {
    const personaId = 'user' in p ? p.user.self_persona.id : p?.persona?.id
    if (this.selectedPersonaId !== personaId) {
      this.selectedPersonaId = personaId
    }
    else {
      this.selectedPersonaId = null
    }
  }

  public onEdit(p: UserProfile): void {
    this.analyticsService.seEvent('Household.EditProfileClick', 8)
    this.editPersonaModal.open(p)
  }

  public onMerge([keepProfile, mergeProfile]: UserProfile[], description: string): void {
    let sourceString
    switch (description) {
      case
        'from_profile_card': sourceString = 'ProfileCard'
      break
      case
        'from_side_panel': sourceString = 'SidePanel'
      break
      case
        'from_banner': sourceString = 'MergeBanner'
      break
      case
        'from_nudge': sourceString = 'NudgeEmail'
      break
    }
    // Do not send traackng event for MergeBanner, we're already sent tracking that in the banner component
    if(sourceString !== 'MergeBanner') {
      this.analyticsService.seEvent(`Household.${sourceString}.Merge`, 8)
    }
    this.mergePersonaModal.open(keepProfile, mergeProfile, description)
  }

  public onNewGuardianModalClose(): void {
    // If the email query param exists, we want to clear it out after the new guardian modal has closed.
    // This is so if the email was passed in as part of the suggested guardian invite flow,
    // the user does not get prompted again after they've closed the modal
    if (this.email) {
      this.clearEmailParam()
    }
  }

  public onRemove(p: UserProfile): void {
    this.analyticsService.seEvent('Household.RemoveProfileClick', 8)
    this.removePersonaModal.open(p)
  }

  public onPersonaCreate(): void {
    this.newPersonModal.open()
    this.analyticsService.seEvent('Household.AddPersonClick', 8)
  }

  public onPersonaEdit(personaId: number): void {
    // trigger getting an updated self across mySE if the self profile image was updated
    const profile = this.profiles.find(p => p.persona.id === personaId)
    if (profile.relationship === 'self' && profile.access === 'owner') {
      this.householdProfilesService.loadSelfPersona()
    }
    this.getHousehold()
  }

  public onPersonaRemove(removedProfile: UserProfile): void {
    this.profiles = this.profiles.filter(p => p.id !== removedProfile.id)
    this._subscriptions.push(this.getGuardians().subscribe(guardians => this.managerGuardians = guardians))
    if (this.selectedPersonaId && this.selectedPersonaId === removedProfile.persona.id) {
      this.selectedPersonaId = null
    }
  }

  public onGuardianUpdate(guardianship: Guardianship): void {
    let updateHousehold$: Observable<any>
    if (guardianship.user.self_persona.id === this.selfProfile.persona.id) {
      updateHousehold$ = this.getProfiles().pipe(tap(profiles => this.profiles = profiles))
    } else {
      updateHousehold$ = this.getGuardians().pipe(tap(guardians => this.managerGuardians = guardians))
    }
    this._subscriptions.push(updateHousehold$.subscribe(_ => {
      if (this.selectedPersonaId) {
        const profile = this.profiles.find(p => p.persona.id === this.selectedPersonaId)
          || this.managerGuardians.find(g => g.user.self_persona.id === this.selectedPersonaId)
        if (!profile) this.selectedPersonaId = null
      }
    }))
  }

  private setIdParam(id: number): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { id },
      queryParamsHandling: 'merge'
    })
  }

  private clearEmailParam(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { email: null },
      queryParamsHandling: 'merge'
    })
    this.email = null
  }
}
